import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Save, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAdminApi, btnPrimary, btnGhost, cardCls } from "./useAdminApi";
import { errMsg } from "@/lib/api";

const ReasonRow = ({ r, i, last, onSave, onMove, onDelete }) => {
  const [d, setD] = useState(r);
  const dirty = d.title !== r.title || d.description !== r.description || Number(d.fee) !== r.fee;
  return (
    <li className="py-4 space-y-2" data-testid={`reason-row-${i}`}>
      <div className="flex gap-2 items-center">
        <Input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} className="h-10 rounded-xl flex-1" data-testid={`reason-title-${i}`} />
        <Input type="number" value={d.fee} onChange={(e) => setD({ ...d, fee: e.target.value })} className="h-10 rounded-xl w-24" data-testid={`reason-fee-${i}`} />
      </div>
      <Input value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder="Short description" className="h-9 rounded-xl text-xs" data-testid={`reason-desc-${i}`} />
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-xs mr-auto"><Switch checked={r.active} onCheckedChange={(v) => onSave({ ...r, active: v })} data-testid={`reason-active-${i}`} />{r.active ? "Shown on website" : "Hidden"}</label>
        <button className={btnGhost} disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`reason-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button className={btnGhost} disabled={last} onClick={() => onMove(i, 1)} data-testid={`reason-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
        <button className={`${btnGhost} text-red-600`} onClick={() => onDelete(r)} data-testid={`reason-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
        <button className={btnPrimary} disabled={!dirty} onClick={() => onSave({ ...d, fee: Number(d.fee) })} data-testid={`reason-save-${i}`}><Save className="h-3.5 w-3.5" />Save</button>
      </div>
    </li>
  );
};

export const ReasonsPanel = () => {
  const call = useAdminApi();
  const [list, setList] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(() => call("get", "/admin/visit-reasons").then(setList), [call]);
  useEffect(() => { load(); }, [load]);

  const put = (r) => call("put", `/admin/visit-reasons/${r.id}`, { title: r.title, description: r.description, fee: r.fee, active: r.active, sort_order: r.sort_order });
  const onSave = async (r) => { try { await put(r); toast.success("Visit reason saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const onMove = async (i, dir) => {
    const a = list[i], b = list[i + dir];
    await Promise.all([put({ ...a, sort_order: b.sort_order }), put({ ...b, sort_order: a.sort_order })]);
    load();
  };
  const onDelete = async (r) => { await call("delete", `/admin/visit-reasons/${r.id}`); toast.success("Removed"); load(); };
  const add = async (e) => {
    e.preventDefault();
    try { await call("post", "/admin/visit-reasons", { title, description: "" }); setTitle(""); toast.success("Visit reason added"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className={cardCls} data-testid="reasons-panel">
      <h3 className="text-xl font-semibold">Visit reasons</h3>
      <p className="text-sm text-ink-muted mt-1">These appear in Step 1 of the booking card, in this order.</p>
      <ul className="divide-y divide-slate-100 mt-2">
        {list.map((r, i) => <ReasonRow key={`${r.id}-${r.sort_order}-${r.fee}-${r.active}`} r={r} i={i} last={i === list.length - 1} onSave={onSave} onMove={onMove} onDelete={onDelete} />)}
      </ul>
      <form onSubmit={add} className="flex gap-2 mt-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New visit reason" className="h-10 rounded-xl" data-testid="reason-new-title" />
        <button type="submit" className={btnPrimary} disabled={title.trim().length < 3} data-testid="reason-add-button"><Plus className="h-4 w-4" />Add</button>
      </form>
    </div>
  );
};
